/**
 * Eval journeys — derive single-turn eval steps from a happy-path transcript.
 *
 * SINGLE SOURCE OF TRUTH for step derivation, shared by the backend (HubDO
 * materialization + capture) and the frontend journey editor. Both surfaces MUST
 * agree on what a "step" is, so the logic lives here in the RN-safe core rather
 * than being re-implemented per consumer.
 *
 * A "journey" is one authored ideal conversation (the transcript). Each
 * user→agent exchange becomes an independent single-turn eval step:
 *
 *   - message_history            : the canonical prefix BEFORE the step's user
 *                                  trigger (incl. prior steps' tool calls +
 *                                  results + texts — grounded context).
 *   - message_text               : the user turn that triggers the step.
 *   - message_expected_response  : the step's FINAL assistant reply, plus EVERY
 *                                  tool call the agent made in the step's run
 *                                  frozen into `expected.tool_calls` (the whole
 *                                  tool loop, even when the run ends in a text
 *                                  reply — gh #2867). This makes the required
 *                                  calls assertable for ACTION evals. The same
 *                                  tool calls + results also appear in the next
 *                                  step's history.
 *
 * Core invariant: every step grades against the IDEAL prefix, never the agent's
 * actual reply from a prior step. Deterministic, no cascade noise.
 *
 * Stable identity: each step is keyed by the `step_id` minted on its concluding
 * assistant turn (see `ensureStepIds`). Re-deriving a journey upserts derived
 * `eval` rows by `(journey_id, step_id)` instead of delete-and-recreate, so
 * historical results stay joinable.
 *
 * A "step" is a contiguous assistant/tool run following a user trigger; the
 * step_id and the per-step config live on ONLY the run's CONCLUDING assistant
 * turn (the final assistant turn before the next user turn). Intermediate
 * assistant(tool_call) turns are NOT steps and carry no id.
 */

import { EVAL_MAX_RUNS_PER_SCENARIO } from './eval-pacing';
import type {
  MessageHistoryItemResponse,
  ExpectedResponseData,
  ToolCallResponse,
  JourneyTranscriptTurnResponse,
  JourneyStepOverrideResponse,
} from './contracts/evals';

/** Per-step `number_of_runs` is capped to bound a session's total dispatch. */
export const MAX_RUNS_PER_STEP = EVAL_MAX_RUNS_PER_SCENARIO;

// The transcript-turn and per-step-override shapes ARE the wire contracts; the
// derivation reuses them so the two can never drift. Re-exported under local
// names for the HubDO + capture + editor consumers.
// - JourneyTranscriptTurn: a message-history item + an optional stable `step_id`
//   on each step's concluding assistant turn (the derived eval's upsert key).
// - JourneyStepOverride: per-step config keyed by `step_id`.
export type JourneyTranscriptTurn = JourneyTranscriptTurnResponse;
export type JourneyStepOverride = JourneyStepOverrideResponse;

/** Inputs for one derived `eval` row. */
export interface JourneyStep {
  journey_step_id: string;
  journey_step_index: number;
  message_history: MessageHistoryItemResponse[];
  message_text: MessageHistoryItemResponse;
  message_expected_response: ExpectedResponseData;
  number_of_runs: number;
  evaluator_instructions: string | null;
}

export interface ExplodeOptions {
  /** Per-step overrides keyed by `step_id`. */
  stepConfig?: Record<string, JourneyStepOverride>;
  /** Journey-level default rubric, applied when a step has no override. */
  defaultEvaluatorInstructions?: string | null;
  /** Journey-level default run count, applied when a step has no override. */
  defaultNumberOfRuns?: number;
}

// Minimal structural shape shared by JourneyTranscriptTurn and the capture
// path's NormalizedMessage, so the expected-merge helper serves both.
export interface AssistantRunItem {
  role: string;
  content: string | null;
  tool_calls?: ToolCallResponse[] | null;
}

interface StepBoundary {
  /** Index of the user turn that triggers the step. */
  triggerIdx: number;
  /** Index of the final assistant turn of the step (the expected response). */
  concludingIdx: number;
}

/**
 * Locate each evaluable step: a contiguous run of assistant/tool turns that
 * follows a user trigger, anchored on the run's final assistant turn. Leading
 * turns with no preceding user (e.g. an opening assistant greeting) and runs
 * with no assistant turn are not steps. A trailing user turn with no agent
 * reply yields no step. Shared by `ensureStepIds` and `explodeTranscriptToSteps`
 * so step identity never drifts between the two — and EXPORTED so the frontend
 * editor derives "Step N" labels + per-step inputs from the same boundaries.
 */
export function findStepBoundaries(transcript: JourneyTranscriptTurn[]): StepBoundary[] {
  const out: StepBoundary[] = [];
  const n = transcript.length;
  let i = 0;
  while (i < n) {
    if (transcript[i].role !== 'assistant') {
      i++;
      continue;
    }
    // Trigger = the last user turn before this assistant run (earlier
    // consecutive user turns fold into history).
    let triggerIdx = -1;
    for (let j = i - 1; j >= 0; j--) {
      if (transcript[j].role === 'user') {
        triggerIdx = j;
        break;
      }
    }
    // Extend over the contiguous assistant/tool run.
    let runEnd = i;
    while (
      runEnd + 1 < n &&
      (transcript[runEnd + 1].role === 'assistant' || transcript[runEnd + 1].role === 'tool')
    ) {
      runEnd++;
    }
    // Concluding turn = last assistant within the run.
    let concludingIdx = -1;
    for (let j = runEnd; j >= i; j--) {
      if (transcript[j].role === 'assistant') {
        concludingIdx = j;
        break;
      }
    }
    if (triggerIdx !== -1 && concludingIdx !== -1) {
      out.push({ triggerIdx, concludingIdx });
    }
    i = runEnd + 1;
  }
  return out;
}

/**
 * Return a copy of the transcript with a stable `step_id` minted on each step's
 * concluding assistant turn that lacks one. Existing ids are preserved (so an
 * edit keeps a step's identity), new turns get fresh ids. Call before storing a
 * journey's transcript; `explodeTranscriptToSteps` then reads the ids back.
 *
 * `crypto.randomUUID()` is available in Workers, browsers, and jsdom — safe for
 * this RN-safe core module.
 */
export function ensureStepIds(transcript: JourneyTranscriptTurn[]): JourneyTranscriptTurn[] {
  const copy = transcript.map((t) => ({ ...t }));
  const seen = new Set<string>();
  for (const b of findStepBoundaries(copy)) {
    const existing = copy[b.concludingIdx].step_id;
    // Mint a fresh id when absent OR when a duplicate — a step copy/pasted in the
    // editor would carry an already-seen id, and step_id is the upsert key, so it
    // MUST be unique. The first occurrence keeps its id (stable identity); the
    // copy becomes a new step.
    if (!existing || seen.has(existing)) {
      copy[b.concludingIdx].step_id = crypto.randomUUID();
    }
    seen.add(copy[b.concludingIdx].step_id as string);
  }
  return copy;
}

/**
 * Build the expected response from the agent exchange that concludes a step.
 * The expected text is the FINAL reply's content; `expected.tool_calls` freezes
 * EVERY tool call the agent made within the step's run (the whole tool loop),
 * walked back from the concluding turn to the triggering user turn. This holds
 * whether the run ends in a tool call (tool-only final turn) OR in a plain text
 * reply that followed a tool loop (tool-then-text, e.g. `book_makeup` → "Done,
 * booked") — both freeze the required calls so an ACTION eval asserts them
 * (gh #2867). Intervening `tool`-result rows of the same exchange are skipped;
 * the walk stops at the trigger user turn so a prior step's calls never bleed
 * in. `tool_calls` is omitted entirely when the run made no calls (pure text
 * step). Shared with `scenario-from-conversation.ts` so capture and journey
 * derivation produce byte-identical expected shapes.
 */
export function buildExpectedFromAssistantRun(
  items: AssistantRunItem[],
  concludingIdx: number,
): ExpectedResponseData {
  const concluding = items[concludingIdx];
  const expected: ExpectedResponseData = { role: 'assistant', content: concluding.content ?? null };
  const calls: ToolCallResponse[] = concluding.tool_calls ? [...concluding.tool_calls] : [];
  for (let i = concludingIdx - 1; i >= 0; i--) {
    const prev = items[i];
    if (prev.role === 'user') break; // trigger boundary — stop before the prior step
    if (prev.role === 'assistant' && prev.tool_calls && prev.tool_calls.length > 0) {
      calls.unshift(...prev.tool_calls);
    }
    // `tool`-result rows and intermediate text-only assistant turns carry no
    // calls — skip them and keep walking the same exchange.
  }
  if (calls.length > 0) expected.tool_calls = calls;
  return expected;
}

// Map a transcript turn to history items, stripping `step_id`. An assistant
// turn carrying BOTH text and tool_calls is split into two items — the replay
// builder (`buildEvalHistoryLlmMessages`) drops text that shares an item with
// tool_calls, so keep them separate to preserve the text.
//
// `attachments` MUST be carried through: this mapper rebuilds turn objects from
// named fields, so an unlisted field silently vanishes from every materialized
// step — the transcript would keep the attachment while the eval rows it
// explodes into lose it (gh #3365). User-only by contract, so the assistant
// split path never carries any.
function toHistoryItems(turn: JourneyTranscriptTurn): MessageHistoryItemResponse[] {
  const hasText = typeof turn.content === 'string' && turn.content.trim() !== '';
  const hasToolCalls = !!turn.tool_calls && turn.tool_calls.length > 0;
  if (turn.role === 'assistant' && hasText && hasToolCalls) {
    return [
      { role: 'assistant', content: turn.content },
      { role: 'assistant', content: null, tool_calls: turn.tool_calls },
    ];
  }
  const item: MessageHistoryItemResponse = { role: turn.role, content: turn.content ?? null };
  if (hasToolCalls) item.tool_calls = turn.tool_calls;
  if (turn.tool_call_id) item.tool_call_id = turn.tool_call_id;
  if (turn.attachments && turn.attachments.length > 0) item.attachments = turn.attachments;
  return [item];
}

function clampRuns(n: number | undefined): number {
  const v = typeof n === 'number' && Number.isFinite(n) ? Math.floor(n) : 1;
  return Math.min(MAX_RUNS_PER_STEP, Math.max(1, v));
}

/**
 * Explode a journey transcript into ordered step-eval inputs. Requires each
 * concluding assistant turn to carry a `step_id` (run `ensureStepIds` first);
 * throws otherwise so a missing id is a loud bug, not a silent orphan.
 */
export function explodeTranscriptToSteps(
  transcript: JourneyTranscriptTurn[],
  opts: ExplodeOptions = {},
): JourneyStep[] {
  const stepConfig = opts.stepConfig ?? {};
  const defaultRuns = opts.defaultNumberOfRuns ?? 1;
  const defaultInstr = opts.defaultEvaluatorInstructions ?? null;

  return findStepBoundaries(transcript).map((b, index) => {
    const stepId = transcript[b.concludingIdx].step_id;
    if (!stepId) {
      throw new Error(
        `explodeTranscriptToSteps: concluding assistant turn at index ${b.concludingIdx} is missing step_id`,
      );
    }
    const trigger = transcript[b.triggerIdx];
    const override = stepConfig[stepId] ?? {};
    return {
      journey_step_id: stepId,
      journey_step_index: index,
      message_history: transcript.slice(0, b.triggerIdx).flatMap(toHistoryItems),
      message_text: {
        role: trigger.role,
        content: trigger.content ?? null,
        ...(trigger.attachments && trigger.attachments.length > 0 && { attachments: trigger.attachments }),
      },
      message_expected_response: buildExpectedFromAssistantRun(transcript, b.concludingIdx),
      number_of_runs: clampRuns(override.number_of_runs ?? defaultRuns),
      evaluator_instructions: override.evaluator_instructions ?? defaultInstr,
    };
  });
}
